import React from 'react';
import {
  Box, Flex, Grid, GridItem, Text, Stat, StatLabel, StatNumber,
  StatHelpText, StatArrow, Icon, useColorModeValue, VStack,
  HStack, Avatar, Heading, Card, CardHeader, CardBody,
  Table, Thead, Tbody, Tr, Th, Td, Badge, TableContainer, Divider
} from '@chakra-ui/react';
import {
  MdReceipt, MdAccountBalance, MdTrendingUp, MdAttachMoney,
  MdCheckCircle, MdWarning, MdSchedule, MdCalculate
} from 'react-icons/md';
import ContabilSidebar from '../Sidebar/ContabilSidebar';
import './ContabilDashboard.css';

const StatCard = ({ icon, label, value, change, changeType, colorScheme }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.300`);

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      shadow="sm"
      transition="all 0.3s"
      _hover={{ shadow: 'md' }}
    >
      <Stat>
        <Flex justify="space-between" align="flex-start">
          <Box>
            <StatLabel fontSize="sm" color="gray.500">{label}</StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold" mt={2}>{value}</StatNumber>
            {change && (
              <StatHelpText mt={2}>
                <StatArrow type={changeType} />
                {change}
              </StatHelpText>
            )}
          </Box>
          <Icon as={icon} fontSize="40px" color={iconColor} />
        </Flex>
      </Stat>
    </Box>
  );
};

const ContabilDashboard = () => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');

  // Sample data for recent entries
  const recentEntries = [
    { id: 1, description: 'Pagamento Precatório #1234', value: 'R$ 45.000,00', type: 'Crédito', status: 'Concluído', date: '12/07/2024' },
    { id: 2, description: 'Taxa Administrativa', value: 'R$ 2.250,00', type: 'Débito', status: 'Pendente', date: '12/07/2024' },
    { id: 3, description: 'Pagamento Precatório #1235', value: 'R$ 120.000,00', type: 'Crédito', status: 'Processando', date: '11/07/2024' },
    { id: 4, description: 'Impostos', value: 'R$ 8.500,00', type: 'Débito', status: 'Concluído', date: '11/07/2024' },
  ];

  return (
    <Flex minH="100vh" bg={bg}>
      <ContabilSidebar />
      
      <Box
        flex="1"
        ml={{ base: 0, lg: "250px" }}
        transition="margin-left 0.3s"
      >
        <Box p={{ base: 4, md: 8 }}>
          {/* Header */}
          <Flex justify="space-between" align="center" mb={8}>
            <Box>
              <Heading size="lg" color={textColor}>
                Dashboard Contábil
              </Heading>
              <Text color="gray.500" mt={1}>
                Bem-vindo de volta, {userInfo?.name || 'Contador'}!
              </Text>
            </Box>
            <HStack spacing={3}>
              <Avatar
                size="md"
                name={userInfo?.name || 'Contábil'}
                src={userInfo?.avatar || ''}
                bg="green.500"
              />
            </HStack>
          </Flex>

          {/* Stats Grid */}
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
            gap={6}
            mb={8}
          >
            <GridItem>
              <StatCard
                icon={MdAttachMoney}
                label="Receita Total"
                value="R$ 3.2M"
                change="18% este mês"
                changeType="increase"
                colorScheme="green"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdReceipt}
                label="Lançamentos"
                value="342"
                change="24 hoje"
                changeType="increase"
                colorScheme="blue"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdAccountBalance}
                label="Saldo Geral"
                value="R$ 850K"
                change="5% esta semana"
                changeType="increase"
                colorScheme="purple"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdCalculate}
                label="Processos Analisados"
                value="89"
                change="12% este mês"
                changeType="decrease"
                colorScheme="orange"
              />
            </GridItem>
          </Grid>

          <Grid
            templateColumns={{ base: '1fr', lg: '3fr 2fr' }}
            gap={6}
          >
            {/* Recent Entries Table */}
            <GridItem>
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Lançamentos Recentes</Heading>
                </CardHeader>
                <CardBody>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Descrição</Th>
                          <Th>Valor</Th>
                          <Th>Tipo</Th>
                          <Th>Status</Th>
                          <Th>Data</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {recentEntries.map((entry) => (
                          <Tr key={entry.id}>
                            <Td>{entry.description}</Td>
                            <Td fontWeight="medium">{entry.value}</Td>
                            <Td>
                              <Badge
                                colorScheme={entry.type === 'Crédito' ? 'green' : 'red'}
                              >
                                {entry.type}
                              </Badge>
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={
                                  entry.status === 'Concluído' ? 'green' :
                                  entry.status === 'Pendente' ? 'orange' :
                                  'blue'
                                }
                              >
                                {entry.status}
                              </Badge>
                            </Td>
                            <Td>{entry.date}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </GridItem>

            {/* Financial Summary */}
            <GridItem>
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Resumo Financeiro</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <HStack>
                          <Icon as={MdCheckCircle} color="green.500" />
                          <Text>Entradas</Text>
                        </HStack>
                        <Text fontWeight="bold" color="green.500">R$ 1.850.000</Text>
                      </HStack>
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <HStack>
                          <Icon as={MdWarning} color="red.500" />
                          <Text>Saídas</Text>
                        </HStack>
                        <Text fontWeight="bold" color="red.500">R$ 425.000</Text>
                      </HStack>
                    </Box>
                    <Divider />
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <HStack>
                          <Icon as={MdTrendingUp} color="blue.500" />
                          <Text fontWeight="medium">Resultado</Text>
                        </HStack>
                        <Text fontWeight="bold" fontSize="lg" color="blue.500">R$ 1.425.000</Text>
                      </HStack>
                    </Box>
                    <Box mt={4}>
                      <Text fontSize="sm" color="gray.500" mb={2}>Pendências</Text>
                      <VStack align="stretch" spacing={2}>
                        <HStack>
                          <Icon as={MdSchedule} color="orange.500" fontSize="16px" />
                          <Text fontSize="sm">12 lançamentos pendentes</Text>
                        </HStack>
                        <HStack>
                          <Icon as={MdCalculate} color="purple.500" fontSize="16px" />
                          <Text fontSize="sm">5 cálculos em análise</Text>
                        </HStack>
                      </VStack>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </Box>
      </Box>
    </Flex>
  );
};

export default ContabilDashboard;
